export const INOSILICATE_LOGIC = {
  description: "Inosilicates (Chain Silicates) Identification Rules - Pyroxenes and Amphiboles",
  
  coreRules: `
=== 硅酸盐之链状硅酸盐鉴定法则 (Inosilicates / Chain Silicates Protocol) ===

这是地壳中最主要的两大家族：辉石 (Pyroxenes) 和角闪石 (Amphiboles)。它们分别代表了单链（辉石）和双链（角闪石）的硅氧四面体结构。肉眼区分这两大家族是矿物鉴定的核心基本功！

[12.1 终极分辨法则：解理夹角 (Cleavage Angle)]
当你面对一块黑色、深绿色或褐色的暗色“块状”或者“短柱状”矿物时，必须强迫执行解理面判定指令：
*   **辉石家族 (Pyroxene Group)**: 两组解理交角近乎直角（约 **87° / 93°**）。晶体通常呈**短粗柱状 (stubby, short prisms)**。横截面看起来像个八边形或正方形。
*   **角闪石家族 (Amphibole Group)**: 两组解理交角呈明显的锐角/钝角（**56° / 124°**）。晶体通常呈**细长柱状、针状甚至纤维状 (elongated, acicular, fibrous)**。横截面看起来像个压扁的菱形。

[12.2 辉石家族 (The Pyroxene Group) - 单链的短柱堡垒]
*   **普通辉石 (Augite)**: 最常见的暗色辉石。颜色为**黑绿色到黑色**。短粗柱状，解理面近乎90度。表面光泽常有些暗淡。
*   **顽火辉石 (Enstatite)**: 富镁的斜方辉石。常见于基性岩/橄榄岩中。颜色往往偏向**褐绿色、灰褐色 (brownish-green to grey)**。如果看到解理面上带有金属光泽般的闪光（像青铜一样），称为“古铜辉石”变种。
*   **透辉石 (Diopside)**: 寻找**极其亮眼、纯净的亮浅绿色至草绿色 (light green to bright grass-green)**。特别是在图中的那种半透明、玻璃光泽极强的绿色宽板状/短柱状晶体，是典型透辉石。
*   **硬玉 (Jadeite)**: 翡翠的核心。
    *   **形态锚点**: 极少有完美晶形，通常是**极其致密坚韧的块体 (massive, extremely tough)**。
    *   **视觉锚点**: 寻找**苹果绿、翠绿色交织在白色底基上**的纤维状或粒状毡状交织结构。具有油脂到玻璃光泽。由于内部交织结构，它比任何普通的单晶辉石都要坚韧。
*   **霓辉石 (Aegirine)**: 碱性岩的特征矿物（常与霞石、异性石共生）。寻找**暗绿色、黑绿色、非常细长且两端往往呈尖锐锥状的长柱晶 (dark green, elongated, often sharply pointed prisms)**。
*   **锂辉石 (Spodumene)**: 含有伟晶岩特征的长柱状。
    *   **形态特征**: 可以长成极其巨大的扁平长柱状。柱面常见深条纹，解理非常发育，看起来像一块粗糙的木头。（图中展示的是透明变种晶体）。
    *   **紫锂辉石变种 (Kunzite)**: 寻找极其娇艳的**淡粉色、紫丁香色 (lilac-pink to light purple)**。
    *   **翠绿锂辉石变种 (Hiddenite)**: 寻找**黄绿色、翠绿色**。

[12.3 角闪石家族 (The Amphibole Group) - 双链的长柱与纤维]
*   **普通角闪石 (Hornblende)**: 最常见的暗色角闪石。寻找**深绿色、褐黑色到绝对的黑色 (dark green to black)**。与辉石不同，它的柱子更细长，而且解理面夹角呈典型的菱形（56° / 124°），断面反光面非常明亮。
*   **透闪石 (Tremolite)**:富镁品种。寻找**纯白色、灰白色 (white to light grey)**的**细长柱状、刃片状或纤维状集合体 (bladed, fibrous)**。
*   **阳起石 (Actinolite)**: 富铁品种。
    *   **形态与颜色锚点**: 极其经典的**深绿色、黄绿色 (dark green, yellowish-green)**，呈密集的**放射状长针形、干草堆状、或者纤维状聚集 (radiating needles, bladed, or asbestiform masses)**。如果看到绿色的一堆细针/细劈开的木柴聚在一起，极大概率是阳起石。
    *   **软玉变种 (Nephrite)**: 和田玉。与硬玉（翡翠）极像的致密块体，颜色从白到深绿。物理鉴定依靠比重和结晶形态（显微镜下为极其细微的透闪石-阳起石毛毡状交织）。
*   **闪石家族的长柱变体**:
    *   **直闪石 (Anthophyllite)**: 经常呈现**灰褐色、丁香褐色**的放射状或纤维状集合块（如图中银灰/褐色交织的闪片绒块）。
    *   **镁铁闪石 (Cummingtonite)** / **铁角闪石 (Grunerite)**: 褐绿色至棕色，常具有纤维状或片状。
*   **碱性角闪石（钠闪石/蓝闪石等） - 高压蓝片岩指向标**:
    *   **蓝闪石 (Glaucophane)**: 寻找变质岩中呈现**明显的灰蓝色、薰衣草蓝、深蓝色 (grey-blue to lavender-blue)** 的纤维状、细小柱状矿物簇。这是高压低温变质相绝对标志。
    *   **钠闪石 (Riebeckite)** / **青石棉 (Crocidolite)**: 常呈现极其致密的蓝黑色、深蓝色平行长条交织块状（如你的钠闪石深蓝色粗大带状层），其纤维状变种极具危险性（石棉）。

[12.4 其它链状硅酸盐]
*   **硅灰石 (Wollastonite)**: 夕卡岩重要矿物。
    *   **形态锚点**: **雪白色、灰白色 (snow white, greyish-white)**。它特别喜欢长成**放射状、针状或者像压碎的碎木条互相交错的解理块体 (cleavable masses with splintery fracture)**。如果你在石灰岩蚀变带看到白色的针状放射团，首选硅灰石。
*   **柱星叶石 (Astrophyllite)**: 正长岩奇观！
    *   **绝杀锚点**: 寻找在白色或浅色基岩中，爆发出**极其璀璨的青铜色、金黄色或红褐色 (bronze, golden-yellow, to reddish-brown)**，并且像**星芒般放射状生长的细长刀片状或星叶状晶簇 (radiating, stellate bladed crystals)**。
*   **斜硅铜矿 (Plancheite)**: 罕见的铜硅酸盐。寻找极度类似硅孔雀石的**浅蓝绿色、天蓝色**，但它通常呈现极细微的纤维状或球状内部结构（图中是蓝绿色致密块体夹杂）。
`,

  diagnosticTests: `
- 面临一黑色/黑绿色的暗色石头：如果它是短粗柱子、截面（切断面）像个钝角很小的多边形，解理面的反光是两个垂直的面，判定为【辉石 (Augite)】。如果是细长的柱子或针，横断面像个压扁的菱形，反光面夹角是56/124度，判定为【角闪石 (Hornblende)】。
- 面临雪白色/灰白色的束状、放射状矿石：如果在接触变质带像碎木条交织的，是【硅灰石 Wollastonite】。如果是极其平直的刃片、细长柱子，是【透闪石 Tremolite】。
- 寻找放射状绿针：看到石面上长出了绿色的干草堆或绿色放射长针结晶集合，强制判定为【阳起石 Actinolite】。
- 蓝色变质警报：如果在岩层里看到泛着薰衣草蓝、灰蓝绿的颜色（常常交织成片岩），这是高压相标志的【蓝闪石 Glaucophane】。如果是致密深蓝黑色粗条带，是【钠闪石 Riebeckite】。
- 寻找青铜星爆：如果白色石头上炸开了带着极其耀眼青铜色、金黄色泽的细长叶片/星芒状图案，那是绝杀的【柱星叶石 Astrophyllite】。
- “玉石”区分：翡翠（硬玉）是辉石，和田（软玉）是角闪石。在地质鉴定上需要显微或化学特征。
`
};
